import { useEffect, useMemo, useState } from 'react';
import TicketBuilder from '../components/TicketBuilder';
import UploadGames from '../components/UploadGames';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { GameCard, PickRow } from '../components/SuggestionsTab';
import NflWinTicket from '../components/NflWinTicket';
import { SafeSection } from '../components/SafeTickets';
import SharePicks from '../components/SharePicks';
import PregameStatus from '../components/PregameStatus';
import PicksNotOnTickets from '../components/PicksNotOnTickets';
import BetModeSwitch from '../components/BetModeSwitch';
import WeeklyLockList from '../components/WeeklyLockList';
import AIPicksTab from '../components/AIPicksTab';
import { ticketIndex, gameOnTicket } from '../lib/sportsPicks';
import { GamesBySlot } from '../components/GamesBySlot';
import PicksCalendar from '../components/PicksCalendar';
import PickRulesPanel from '../components/PickRulesPanel';
import FootballLegStats from '../components/FootballLegStats';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const TicketBadge = () => <span className="ml-1.5 rounded bg-sky-500 px-1.5 py-0.5 text-[9px] font-bold text-black align-middle" data-testid="game-on-ticket-badge">ON YOUR TICKET</span>;

const ExtraLegs = ({ g, picks, idx }) => {
  const extra = picks.filter((p) => p.event_id === g.event_id && p.prob >= 0.7 && !idx.players.has(p.player.toLowerCase())).slice(0, 6);
  if (!extra.length) return null;
  return (
    <div className="rounded-lg border border-sky-500/40 bg-sky-950/20 p-2" data-testid={`extra-legs-${g.event_id}`}>
      <p className="text-xs font-bold text-sky-300">Extra legs for this game (not already on your tickets)</p>
      {extra.map((x, i) => <PickRow key={`${x.player}-${x.stat}-${i}`} x={x} testId={`extra-leg-${g.event_id}-${i}`} />)}
    </div>
  );
};

const Step = ({ n, title, sub }) => (
  <div className="flex items-baseline gap-2 pt-2" data-testid={`nfl-step-${n}`}>
    <span className="rounded-full bg-amber-500 text-black w-5 h-5 shrink-0 text-[11px] font-bold flex items-center justify-center">{n}</span>
    <div><p className="text-sm font-bold text-white">{title}</p><p className="text-[11px] text-zinc-500">{sub}</p></div>
  </div>
);

export default function TodaysPicksPage() {
  const [board, setBoard] = useState(null);
  const [parlays, setParlays] = useState([]);
  const tab = 'nfl';
  useEffect(() => {
    axios.get(`${API}/board`).then((r) => setBoard(r.data)).catch(() => setBoard({ error: true }));
    axios.get(`${API}/parlays`).then((r) => setParlays(r.data.parlays || [])).catch(() => {});
  }, []);
  const idx = useMemo(() => ticketIndex(parlays), [parlays]);
  const view = useMemo(() => {
    if (!board?.nfl) return null;
    const sortT = (list, playersOf) => [...list].map((g) => ({ g, t: gameOnTicket(g, idx, playersOf(g)) }))
      .sort((a, b) => (b.t - a.t) || String(a.g.date).localeCompare(String(b.g.date)));
    return {
      nfl: sortT(board.nfl.games, (g) => g.players || []),
      cfb: sortT(board.cfb.games, () => []),
      safe: board.safe,
    };
  }, [board, idx]);
  if (sessionStorage.getItem('sports_betting_authed') !== 'true') return <Navigate to="/sports-betting" replace />;

  const renderGame = (g, onT) => (
    <GameCard key={g.event_id} g={g} badge={onT ? <TicketBadge /> : null} ticketNames={idx.players}
      extra={onT ? <ExtraLegs g={g} picks={board.nfl.picks} idx={idx} /> : null} />);

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-6" data-testid="todays-picks-page">
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/sports-betting" className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="back-to-tickets"><ArrowLeft className="w-4 h-4" /> My tickets</Link>
          <div className="text-right">
            <h1 className="text-lg md:text-xl font-bold flex items-center gap-2 justify-end"><Sparkles className="w-5 h-5 text-amber-400" />🏈 NFL Picks</h1>
            <p className="text-[11px] text-zinc-500">Next NFL game day · tap a game for its suggestions</p>
            <div className="mt-1 flex justify-end"><BetModeSwitch compact /></div>
          </div>
        </div>
        <PicksCalendar league="nfl" />
        <PickRulesPanel />
        <FootballLegStats />
        {!board && <div className="flex justify-center gap-2 py-16 text-zinc-400" data-testid="board-loading"><Loader2 className="w-5 h-5 animate-spin" />Loading this week's games…</div>}
        {board?.error && <p className="text-sm text-red-400" data-testid="board-error">Could not load games. Try again in a minute.</p>}
        {view && (
          <>
            {board.alerts?.length > 0 && (
              <div className="rounded-xl border border-orange-500/50 bg-orange-950/30 p-3 space-y-0.5" data-testid="kickoff-alerts">
                <p className="text-sm font-bold text-orange-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Injury updates</p>
                {board.alerts.slice(-5).map((a) => <p key={a.msg} className="text-xs text-zinc-300">• {a.msg} — replaced with the next-safest pick</p>)}
              </div>
            )}
            <TicketBuilder league="nfl" />
            <UploadGames league="nfl" />
            <SharePicks />
            <PregameStatus leagues={['nfl']} />
            {board.nfl.slate && (
              <p className="text-sm font-semibold text-amber-300" data-testid="board-slate">
                NFL · {board.nfl.slate.label} only · moves to the next game day once these games end
              </p>
            )}
            <Step n="1" title="Ready-made safe tickets" sub="Tickets built from the safest picks — tap Track to follow one" />
            <SafeSection title={`NFL · ${board.nfl.slate?.label || `Week ${board.nfl.week || ''}`} · safe tickets`} tickets={view.safe.nfl} league="nfl" amount={board.settings?.ticket_amount} />
            <Step n="2" title="Who wins each game" sub="Just pick the winner — no point spreads" />
            <NflWinTicket wins={board.nfl.wins} slate={board.nfl.slate} />
            <Step n="3" title="Picks not on my tickets" sub="New picks for players and games you don't already have" />
            <PicksNotOnTickets />
            <Step n="4" title="Every game, player by player" sub="Tap a game, then a player, to see his numbers" />
            {board.nfl.building && !view.nfl.length && <p className="text-xs text-zinc-400">NFL suggestions are updating — refresh in a minute.</p>}
            <GamesBySlot games={view[tab].map((x) => ({ ...x.g, _onT: x.t }))} testId="nfl-slots" render={(g) => renderGame(g, g._onT)} />
            {!view[tab].length && <p className="text-xs text-zinc-500" data-testid="board-no-games">No NFL games left on this game day.</p>}
            <details className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3" data-testid="nfl-second-opinion">
              <summary className="cursor-pointer text-sm font-bold text-zinc-200">Second opinion — near-locks list + AI written game picks</summary>
              <p className="text-[11px] text-zinc-500 mt-1">A separate check that reads the matchups and writes out its own picks. Use it to double-check the picks above.</p>
              <div className="mt-2 space-y-3"><WeeklyLockList only="nfl" /><AIPicksTab fixedLeague="nfl" /></div>
            </details>
          </>
        )}
      </div>
    </div>
  );
}
